<template>
    <tailwind-layout title="Rental Payments">
        <template #header>
            <div class="px-4 sm:px-6 lg:px-8 mx-auto hidden lg:flex justify-between items-center"> <!-- Block for lg and larger -->
                <h1 class="font-semibold text-2xl leading-tight">Rental Payments</h1>
            </div>

            <div class="mb-3 px-6 lg:px-8 mx-auto flex flex-col lg:hidden"><!-- Block for sm, md, lg (buttons will stack on smaller screens) -->
                <div class="flex justify-between items-center">
                    <h1 class="font-semibold text-2xl leading-tight">Rental Payments</h1>
                </div>
            </div>

        </template>
        <div class="overflow-hidden">
            <div
                v-for="(property, index) in properties.data" :key="index"
                class="px-4 pt-5 pb-0 sm:pb-5 bg-white overflow-hidden sm:rounded-lg p-6 max-w-7xl mb-2">
                <div>
                    <div class="sm:flex flex-row justify-between border-b mb-4">
                        <h1 class="text-lg font-bold text-black">{{ property.full_address }}</h1>
                        <h2><span class="text-lg font-bold text-black">Lease Term:</span> {{ property.rent_info.lease_start_date+' - '+property.rent_info.lease_end }} ( {{ property.rent_info.remaining_months }} )</h2>
                    </div>
                    <div class="sm:flex flex-row gap-x-4">
                        <div class="bg-gray-200 p-6 sm:rounded-lg">
                            <h2 class="text-lg font-bold text-black">Landlord</h2>
                            <ul v-if="property.agent">
                                <li><span class="font-bold">Name: </span>{{ property.agent.name }}</li>
                                <li><span class="font-bold">Email: </span> <a href="mailto:{{ property.agent.email }}" class="underline text-blue-600">{{ property.agent.email }}</a></li>
                                <li><span class="font-bold">Home Phone: </span> <a href="tel:{{ property.agent.home_phone }}" class="underline text-blue-600">{{ property.agent.home_phone }}</a></li>
                                <li><span class="font-bold">Cell Phone: </span> <a href="tel:{{ property.agent.cell_phone }}" class="underline text-blue-600">{{ property.agent.cell_phone }}</a></li>
                                <li><span class="font-bold">Office Phone: </span> <a href="tel:{{ property.agent.office_phone }}" class="underline text-blue-600">{{ property.agent.office_phone }}</a></li>
                            </ul>
                            <p v-else class="text-gray-600">Landlord information is not available.</p>
                        </div>
                        <div class="mt-2 sm:mt-0">
                            <h2>Monthly Rent: ${{ property.rent }}</h2>
                            <h2>Lease Term: {{ property.rent_info.lease_start_date+' - '+property.rent_info.lease_end }}</h2>
                            <h2>Rent Starts From: {{ property.rent_info.lease_start }}</h2>
                            <h2>Status:
                                <span :class="(property.rent_info.status === 'Active') ? 'inline-flex items-center rounded-md bg-green-50 ml-2 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20' : 'inline-flex items-center rounded-md bg-red-50 px-2 py-1 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-600/10'">
                                                        {{ property.rent_info.status }}
                                                    </span>
                            </h2>
                        </div>
                    </div>
                    <div class="sm:flex mt-5">
                        <h2 class="mt-1">Rent Late On: {{ property.rent_info.rent_late_on }} <span class="bg-yellow-400 rounded pl-1 pr-1 text-xs">Late Charges will be assessed after this date.</span></h2>
                        <div class="hidden sm:block">
                            <inertia-link
                                v-if="property.rent_info.tenant_id === $page.props.user.id || $page.props.user.role === 'agent'"
                                title="View Monthly Rental Payments"
                                :href="route('rented-properties.payments', property.rent_info.id)"
                                class="relative inline-flex items-center rounded-md bg-blue-50 px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-blue-200 focus:z-10 ml-2">
                                View Current and Upcoming Payments
                            </inertia-link>
                            <inertia-link
                                title="Property Transactions"
                                :href="route('rented-properties.property-transactions', property.id)"
                                class="relative inline-flex items-center rounded-md bg-blue-50 px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-blue-200 focus:z-10 ml-2">
                                Transactions
                            </inertia-link>
                        </div>
                        <div class="sm:hidden mt-3 -mx-4">
                            <div :class="[property.deleted_at ? 'divide-red-300' : 'divide-gray-200','-mt-px flex divide-x']">
                                <div class="flex w-0 flex-1">
                                    <inertia-link
                                        v-if="property.rent_info.tenant_id === $page.props.user.id || $page.props.user.role === 'agent'"
                                        title="View Monthly Rental Payments"
                                        :href="route('rented-properties.payments', property.rent_info.id)"
                                        class="hover:bg-blue-200 relative -mr-px inline-flex w-0 flex-1 items-center justify-center gap-x-3 rounded-bl-lg border border-transparent py-4 text-sm font-semibold text-gray-900">
                                        <span class="ml-2">
                                            <font-awesome-icon icon="fa-eyes" />
                                            View Current and Upcoming Payments
                                        </span>
                                    </inertia-link>
                                </div>
                                <div class="-ml-px flex w-0 flex-1">
                                    <inertia-link
                                        title="Property Transactions"
                                        :href="route('rented-properties.property-transactions', property.id)"
                                        class="hover:bg-blue-200 relative inline-flex w-0 flex-1 items-center justify-center gap-x-3 rounded-br-lg border border-transparent py-4 text-sm font-semibold text-gray-900">
                                        <font-awesome-icon icon="fa-money-check" />
                                        Transactions
                                    </inertia-link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div v-show="!properties.data.length" class="px-4 py-5 bg-white bg-rent-risk-blue-100 overflow-hidden sm:rounded-lg p-6 max-w-7xl mb-2">
                <div>
                    Your landlord has accepted your invitation. Please wait for them to assign your rental property to get started with rent payments.
                </div>
            </div>
        </div>
    </tailwind-layout>
</template>

<script>
import TailwindLayout from '@/Layouts/TailwindLayout.vue'

export default {
    components: {
        TailwindLayout
    },
    props: {
        properties: Object,
    },
}
</script>
